<script setup lang="ts">
import { Link, usePage } from "@inertiajs/vue3"
import { computed, watch } from "vue"
import { UiToast } from "@/components"
import { useNotifications } from "@/composables/useNotifications"

interface FlashProps {
  notice?: string
  alert?: string
}

const props = defineProps<{
  title: string
  subtitle?: string
}>()

const page = usePage<{ url: string; flash?: FlashProps }>()
const currentPath = computed(() => page.url.split("?")[0])
const notify = useNotifications()
let lastFlashKey = ""

const notice = computed(() => {
  const flashNotice = page.props.flash?.notice
  if (!flashNotice) return ""

  // Avoid showing a noisy logout toast on the login screen.
  if (currentPath.value === "/users/sign_in" && flashNotice.toLowerCase().includes("signed out")) return ""
  return flashNotice
})
const alert = computed(() => page.props.flash?.alert)

watch(
  () => [notice.value, alert.value, currentPath.value],
  ([noticeMessage, alertMessage]) => {
    const key = `${noticeMessage || ""}::${alertMessage || ""}`
    if (!key || key === "::" || key === lastFlashKey) return

    if (noticeMessage) notify.success(noticeMessage)
    if (alertMessage) notify.error(alertMessage)

    lastFlashKey = key
  },
  { immediate: true }
)

const authLinks = [
  { href: "/users/sign_in", label: "Entrar" },
  { href: "/users/password/new", label: "Esqueci a senha" },
  { href: "/users/confirmation/new", label: "Reenviar confirmacao" },
]

const visibleLinks = computed(() => authLinks.filter((link) => link.href !== currentPath.value))
</script>

<template>
  <main class="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-8 sm:px-6">
    <section class="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-300/35 sm:p-7">
      <header class="mb-5 text-center py-4">
        <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Fabrik</p>
        <h1 class="mt-2 text-2xl font-semibold text-slate-900">{{ props.title }}</h1>
        <p v-if="props.subtitle" class="mt-2 text-sm text-slate-600">{{ props.subtitle }}</p>
      </header>

      <slot />

      <nav v-if="visibleLinks.length" class="mt-5 flex flex-wrap items-center justify-center gap-3 border-t border-slate-200 pt-4 text-sm">
        <Link
          v-for="link in visibleLinks"
          :key="link.href"
          :href="link.href"
          class="rounded-md px-2 py-1 text-indigo-600 transition hover:bg-indigo-50 hover:text-indigo-700"
        >
          {{ link.label }}
        </Link>
      </nav>
    </section>

    <UiToast />
  </main>
</template>
